import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import usePurchaseTicket from '../../../hooks/usePurchaseTicket';
import { ORDER_STATUS, PAYMENT_TYPE, PAYPAY_POOLING } from '../../../utils/Constants';
import useR05Api from '../../../api/useR05Api';
import styles from './styles.module.css';
import { retryWithTimeoutAndCondition, showNotificationDialog } from '../../../utils';
import { OrderTicket } from 'types';

const BuyCreditCardCertificateScreen = () => {
    const [fetchOrderLoading, setFetchOrderLoading] = useState(false);
    const [isFailed, setIsFailed] = useState(false);
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { isLoading, completePurchase } = usePurchaseTicket();
    const { fetchOrderDetail } = useR05Api();
    const navigate = useNavigate();
    const shouldContinue = useRef(true);

    const navigateToSuccessScreen = useCallback(
        (orderId: any) => {
            if (orderId) {
                navigate('/buy/ticket/success', { state: { orderId: orderId } });
            }
        },
        [navigate],
    );

    useEffect(() => {
        try {
            const paymentMethod = searchParams.get('paymentMethod') ?? null;
            if (paymentMethod === PAYMENT_TYPE.paypay.toLocaleLowerCase()) {
                const orderId = searchParams.get('orderId') ?? null;
                if (orderId === null) return;
                void (async () => {
                    // 支払いが未完了の場合
                    // PayPayの完了まで
                    setIsFailed(false);
                    setFetchOrderLoading(true);
                    const orderTicket: OrderTicket | undefined = await retryWithTimeoutAndCondition(
                        async () => {
                            return await fetchOrderDetail({
                                orderId: orderId,
                            });
                        },
                        PAYPAY_POOLING.retryCount,
                        PAYPAY_POOLING.timeout,
                        (result: OrderTicket) => result?.orderStatus !== ORDER_STATUS.PENDING,
                    );
                    setFetchOrderLoading(false);

                    if (orderTicket?.orderStatus === ORDER_STATUS.BOUGHT) {
                        navigateToSuccessScreen(orderId);
                    } else if (orderTicket?.orderStatus === ORDER_STATUS.FAILED) {
                        setIsFailed(true);
                        showNotificationDialog('error', '', t('notification.title'), t('error-message.process-error'));
                    } else {
                        showNotificationDialog(
                            'success',
                            '',
                            t('notification.title'),
                            t('notification.purchase-timeout'),
                        );
                    }
                })();
            } else {
                const p = searchParams.get('p') ?? null;
                if (p && shouldContinue.current) {
                    void (async () => {
                        // 支払いが未完了の場合
                        // クレジットカードの完了まで
                        setIsFailed(false);
                        shouldContinue.current = false;
                        const { orderId } = (await completePurchase(p)) ?? {};
                        if (orderId === null) {
                            setIsFailed(true);
                            return;
                        }
                        navigateToSuccessScreen(orderId);
                    })();
                }
            }
        } catch (error) {
            console.error(error);
            setIsFailed(true);
        }
    }, [t, completePurchase, fetchOrderDetail, navigateToSuccessScreen, searchParams, shouldContinue]);

    return (
        <>
            <Header title={t('header.buy-ticket')} redirect={'/buy'} />
            <div className={`${styles.wrapper} content-container scrollable`}>
                {(isLoading || fetchOrderLoading) && <CenterLoading overFooter />}
                <p>
                    {isLoading || fetchOrderLoading
                        ? t('payment.in-progress')
                        : isFailed && t('payment.purchase-failed')}
                </p>
            </div>
        </>
    );
};

export default React.memo(BuyCreditCardCertificateScreen);
